import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'

export function useViolinChart(root, violinRow, chartData) {
    let chart
    let xAxis
    let scrollableContainer

    const configureViolin = (options) => {
        root.setThemes([am5themes_Animated.new(root)])

        chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: 'none',
                wheelY: 'none',
                pinchZoomX: false,
                pinchZoomY: false,
                maxTooltipDistance: -1,
                paddingTop: 40,
            })
        )

        if (options?.scrollContainer) {
            scrollableContainer = chart.chartContainer.children.unshift(
                am5.Container.new(root, {
                    width: am5.p100,
                    height: am5.p100,
                    verticalScrollbar: am5.Scrollbar.new(root, {
                        orientation: 'vertical',
                    }),
                    wheelable: true,
                })
            )

            chart.yAxesAndPlotContainer.set('height', violinRow.length * 60)
            chart.yAxesAndPlotContainer.set('paddingBottom', 10)
            scrollableContainer.children.push(chart.yAxesAndPlotContainer)
        }
    }

    const generateXAxes = (xData, options) => {
        const snapToTicks = options?.snapToTicks !== false

        xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: 'year',
                renderer: am5xy.AxisRendererX.new(root, {
                    minGridDistance: 20,
                    cellStartLocation: snapToTicks ? 0.1 : 0,
                    cellEndLocation: snapToTicks ? 0.9 : 1,
                }),
                tooltip: am5.Tooltip.new(root, {}),
            })
        )

        let xRenderer = xAxis.get('renderer')

        xRenderer.labels.template.setAll({
            rotation: -45,
            location: 0.5,
            multiLocation: 0.5,
            centerX: am5.p100,
            centerY: am5.p50,
            fontSize: 12,
        })

        xRenderer.grid.template.setAll({
            location: 0.5,
            multiLocation: 0.5,
        })

        if (!snapToTicks) {
            xAxis.set('strictMinMax', true)
            xAxis.set('startLocation', 0)
            xAxis.set('endLocation', 1)
        }

        xAxis.data.setAll(xData)
        chart.leftAxesContainer.setAll({
            layout: root.verticalLayout,
        })
    }

    const createAllSeries = (options) => {
        const xData = processData()
        generateXAxes(xData)
        const globalBounds = findGlobalBounds()

        violinRow.forEach((row) => {
            const series = createSeries(row, globalBounds, options)
            series.data.setAll(processData(row.displayName))

            // Add target bullet if targetItem is provided
            if (options?.targetItem && options.targetItem.date) {
                addTargetBullet(series, row, options.targetItem.date)
            }
        })

        addCursor(options)
    }

    // Create a bullet series for the target date
    const addTargetBullet = (series, row, targetDate) => {
        const bulletSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                xAxis: xAxis,
                yAxis: series.get('yAxis'),
                valueYField: 'y',
                categoryXField: 'year',
                stroke: am5.color('#F2AF3A'),
            })
        )

        bulletSeries.bullets.push(function () {
            const line = am5.Rectangle.new(root, {
                width: 5,
                height: 30,
                fill: am5.color('#F2AF3A'),
                centerX: am5.p50,
                centerY: am5.p50,
                tooltipText: `Filing Date: [bold]${new Date(targetDate).toLocaleDateString()}[/]`,
            })

            const tooltip = am5.Tooltip.new(root, {
                pointerOrientation: 'up',
                labelText: `Filing Date: [bold]${new Date(targetDate).toLocaleDateString()}[/]`,
                dy: 20,
            })

            line.set('tooltip', tooltip)

            const container = am5.Container.new(root, {})
            container.children.push(line)

            return am5.Bullet.new(root, {
                sprite: container,
            })
        })

        // Find the closest year to the target date
        const targetYear = new Date(targetDate).getFullYear().toString()
        bulletSeries.data.setAll([
            {
                year: targetYear,
                y: 0,
            },
        ])

        // Make sure this series is drawn on top and has priority
        bulletSeries.set('zIndex', 1000)
        bulletSeries.set(
            'tooltipText',
            `[bold]${row.displayName}[/]\nFiling Date: [bold]${new Date(targetDate).toLocaleDateString()}[/]`
        )

        // Ensure tooltips work properly
        bulletSeries.set('cursorOverStyle', 'pointer')
        bulletSeries.set('tooltipPosition', 'fixed')
    }

    const createSeries = (row, globalBounds, options) => {
        let yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                maxDeviation: 0,
                strictMinMax: true,
                extraMin: 0.05,
                extraMax: 0.05,
                renderer: am5xy.AxisRendererY.new(root, {}),
                min: globalBounds.low,
                max: globalBounds.high,
            })
        )

        configureYAxisRenderer(yAxis)
        const series = createAndConfigureSeries(yAxis, row, options)
        addLabel(yAxis, row, series, options)

        return series
    }

    const configureYAxisRenderer = (yAxis) => {
        let yRenderer = yAxis.get('renderer')
        yRenderer.labels.template.setAll({
            forceHidden: true,
        })
        yRenderer.grid.template.setAll({
            forceHidden: true,
        })
    }

    const createAndConfigureSeries = (yAxis, row, options) => {
        let series = chart.series.push(
            am5xy.SmoothedXLineSeries.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: 'high',
                openValueYField: 'low',
                categoryXField: 'year',
                fill: am5.color(row.color),
                tooltip: am5.Tooltip.new(root, {}),
            })
        )
        const scoreLabel = options?.scoreLabel || 'Moat Coverage'

        series
            .get('tooltip')
            .label.set('text', `[bold]${row.displayName}[/]\n${scoreLabel}: [bold]{count}[/]`)

        series.fills.template.setAll({
            fillOpacity: 1,
            visible: true,
        })

        return series
    }

    const addLabel = (yAxis, row, series, options) => {
        const displayText = row.displayName
        let truncatedText = displayText
        if (options?.truncate) {
            const labelLength = options.labelLength || 15
            truncatedText =
                displayText.length > labelLength
                    ? displayText.substring(0, labelLength) + '...'
                    : displayText.substring(0, labelLength)
        }

        yAxis.children.unshift(
            am5.Label.new(root, {
                text: truncatedText,
                y: am5.percent(16),
                x: am5.percent(100),
                centerX: am5.percent(100),
                fill: am5.color(0x000000),
                fontWeight: '600',
            })
        )
    }

    const addCursor = (options) => {
        const cursor = am5xy.XYCursor.new(root, {
            behavior: 'none',
        })

        // Make the cursor more visible
        cursor.lineX.setAll({
            visible: true,
            strokeDasharray: [2, 2],
        })

        chart.set('cursor', cursor)
        chart.appear(1000, 100)
    }

    const processData = (entityIdentifier = null) => {
        const filteredData = entityIdentifier
            ? chartData.filter(
                  (item) =>
                      item.aonEntityName === entityIdentifier ||
                      item.displayName === entityIdentifier
              )
            : chartData

        return filteredData
            .reduce((acc, curr) => {
                const existingYear = acc.find((item) => item.filingYear === curr.filingYear)
                if (existingYear) {
                    existingYear.count += curr.powerScore
                } else {
                    acc.push({
                        filingYear: curr.filingYear,
                        count: curr.powerScore,
                    })
                }
                return acc
            }, [])
            .map((item) => ({
                year: item.filingYear.toString(),
                count: Math.ceil(item.count),
                high: Math.ceil(item.count),
                low: Math.ceil(item.count) * -1,
            }))
    }

    const findGlobalBounds = () => {
        const powerScores = Math.max(...chartData.map((item) => item.powerScore))
        return {
            high: Math.ceil(powerScores),
            low: Math.ceil(powerScores) * -1,
        }
    }

    const disposeViolin = () => {
        if (chart && !chart.isDisposed()) {
            if (root && !root.isDisposed()) {
                root?.setThemes([])
            }
            chart.series?.clear()
            chart.yAxes?.clear()
            chart.get('cursor')?.dispose()
            scrollableContainer?.dispose()
            xAxis?.dispose()
            chart.dispose()
        }
    }

    return {
        configureViolin,
        createAllSeries,
        disposeViolin,
    }
}
